// §4 S5 — Shift scoreboard, /production/shifts (Module 04, slice 04-C-4).
//
// THE SCREEN IS ONE QUESTION: which shift, at which station, made what — and
// how much of it was right first time. §3.7's whole anti-gaming mechanic is the
// number in the FPY column, so the design's only real job is to make a bad
// yield impossible to miss and impossible to misread.
//
// WHAT THE DESIGN COMMITS TO, and what each commitment is protecting against:
//
//  * WORST-FPY TINTING, NEVER COLOUR ALONE. The row with the worst first-pass
//    yield in the visible set carries a tint AND a worded badge AND a rank in
//    the accessible name. A supervisor reading a corridor screen in daylight,
//    or with red-green colour blindness, must reach the same row as everyone
//    else. (dataviz: status is an icon + words; colour is the accelerant.)
//  * NULL YIELD IS NOT ZERO YIELD. A shift that produced nothing has no yield
//    (§2.10: null when the denominator is 0). It renders as “—”, is excluded
//    from the worst-row search, and never wears the tint. Showing 0% there
//    would put the quietest station permanently at the top of the wall of
//    shame.
//  * `finalized` IS ON THE ROW. A live shift's numbers are the incremental
//    ones and can still move; a settled shift's were recomputed from source by
//    the §3.7 close job. Those are different claims and the screen says which.
//  * THE DEFECT COLUMNS WERE PRESENT AND ZERO BEFORE THEY WERE WRITABLE, which
//    is why they need no schema change now. Omitting them would have been the
//    worse claim: present-and-empty says "this is measured and the answer is
//    zero", absent says "this is not measured here" — false the moment C-5's
//    writer landed.
//  * EVERY ROW SAYS WHICH SHIFT. §4's S5 is "one row per (shift, work centre)",
//    and a date with a DAY and a NIGHT shift therefore returns the same station
//    twice. The shift code rides the ROW (e17) rather than being joined from
//    e24 client-side, because e24 is gated on `production.read` and this screen
//    on `production.scoreboard.read` — D-074's grammar admits a custom role
//    holding only the latter, and it would have got a board of anonymous rows.
//  * THE HAND-OFF LEDGER IS REAL (04-C-5). §4's S5 pairs the scoreboard with
//    the defect hand-off panel; it was a stated-pending note naming its owner
//    while `defect_handoffs` did not exist, and it is the live ledger now, with
//    §5 e19's three dispositions as three buttons a supervisor clicks. An
//    action only the API can reach is not shipped — this screen's own §4 head
//    notes record three separate occasions when that lesson was learned here.
//  * THE TWO DEFECT COLUMNS MOVE NOW, and the note under the table says what
//    they MEAN rather than why they are zero. A defect landing on a shift that
//    closed yesterday looks like a bug until somebody explains that it is the
//    point.
//
// NO MONEY ANYWHERE, and not because it was redacted: §2.10 gives the
// scoreboard no cost columns at all. A board read off a wall by whoever walks
// past cannot carry a column half its audience may not see.
//
// Roles: group_admin / facility_admin / planner / finance / qc / maintenance
// (`production.scoreboard.read`), plus the floor variant the S12 wall board
// rides (D-087 ruling 1).

import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { HelpTip } from "../../../../components/help-tip";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { joinSentences } from "../../../../lib/sentences";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  useFacilities,
  useShiftScoreboard,
  useWorkCenters,
  type DefectHandoffQuery,
  type ShiftScoreboardRow,
} from "../hooks/production-queries";
import { formatQty, localeLabel } from "../lib/format";
import { FacilityFilter } from "../components/facility-filter";
import { HandoffLedger } from "../components/handoff-ledger";
import { RefLabel } from "../components/ref-label";
import { useProductionPermissions } from "../hooks/permissions";

// `facilityTodayIso` (and its browser-calendar fallback `todayIso`) moved to
// the shared home `src/lib/business-dates.ts` when the D-089 rule outgrew this
// screen — the cost-run default (module 01 S7) and the BOM/routing activation
// floors take the same pair. The rule and its history live on the helpers.
import { facilityTodayIso } from "../../../../lib/business-dates";

/**
 * The row with the WORST first-pass yield among rows that HAVE one. Null yield
 * is excluded by construction — a shift that produced nothing has no yield, and
 * ranking it worst would put the quietest station permanently at the top of the
 * board. Ties resolve to the first row in server order, so the highlight is
 * stable between polls. A ranking needs a line to fall below another (D-449):
 * with one line that has a yield, or every line at the same yield (two at 100%
 * is a healthy board), there is no worst, so nothing is flagged.
 */
export function worstYieldRowKey(rows: readonly ShiftScoreboardRow[]): string | null {
  let worst: { key: string; pct: number } | null = null;
  let best: number | null = null;
  for (const row of rows) {
    if (row.first_pass_yield_pct === null) continue;
    const pct = Number(row.first_pass_yield_pct);
    if (Number.isNaN(pct)) continue;
    if (best === null || pct > best) best = pct;
    if (worst === null || pct < worst.pct) {
      worst = { key: rowKey(row), pct };
    }
  }
  return worst !== null && best !== null && worst.pct < best ? worst.key : null;
}

export function rowKey(row: ShiftScoreboardRow): string {
  return `${row.shift_instance_id}:${row.work_center_id}`;
}

export function ShiftsScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();

  const permissions = useProductionPermissions();
  const facilities = useFacilities();
  const [facilityId, setFacilityId] = useState<string | undefined>(undefined);
  const effectiveFacility = facilityId ?? facilities.data?.items[0]?.id;
  // The DEFAULT date is the facility's today, not the viewer's (§9.7 /
  // facilityTodayIso above): an office viewer west of the plant opening this
  // screen during the plant's early morning otherwise lands on a date the
  // running shift is not filed under. It stays a DEFAULT — the picker's answer
  // wins from the first touch — so it is derived, not captured in state, and
  // it follows a facility switch until the user has picked.
  const [pickedDate, setPickedDate] = useState<string | undefined>(undefined);
  const facilityTimezone = facilities.data?.items.find((f) => f.id === effectiveFacility)?.timezone;
  const shiftDate = pickedDate ?? facilityTodayIso(Date.now(), facilityTimezone);
  // NO status filter: a work centre archived mid-shift still has rows on the
  // shift it worked, and an "active only" lookup would leave them anonymous on
  // the screen that reports that shift.
  const workCenters = useWorkCenters(effectiveFacility);

  const board = useShiftScoreboard(
    {
      shift_date: shiftDate,
      ...(effectiveFacility !== undefined ? { facility_id: effectiveFacility } : {}),
    },
    effectiveFacility !== undefined,
  );

  useEffect(() => {
    if (board.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [board.error, navigate]);

  const rows = board.data?.rows ?? [];
  // THE INSTANCES ON SCREEN — all of them, as a set. §5 e18's
  // `shift_instance_id` takes a comma-separated set and matches EITHER end, so
  // the panel is scoped to exactly the shifts the board is showing.
  //
  // IT USED TO NARROW ON A DAY/NIGHT DATE, and that was the defect: with two
  // instances there was no single id to scope to, so it fell back to the
  // facility's PENDING queue — which meant a hand-off VANISHED the moment it
  // was decided (it left the pending filter), no screen anywhere could list the
  // decided hand-offs for that date, and the empty-state copy ("No hand-offs on
  // this shift date") was false for a query carrying no date at all. A day/night
  // date is not an edge case here; it is the arrangement §3.7's whole mechanic
  // exists for. e18 gained the set filter rather than this screen gaining a
  // second question (adversarial review, 2026-08-07).
  const shiftInstanceIds = [...new Set(rows.map((r) => r.shift_instance_id))];
  const handoffQuery: DefectHandoffQuery | undefined =
    shiftInstanceIds.length > 0
      ? { shift_instance_id: shiftInstanceIds.join(","), limit: 50 }
      : undefined;
  const totals = board.data?.facility_totals ?? [];
  const worstKey = worstYieldRowKey(rows);
  // A station the work-centre read has not answered for is NOT its uuid: the
  // board used to print `0191f3c1-…` in the row header where a machine name
  // belongs, and it printed the same thing whether the list was still loading
  // or the station genuinely did not resolve (builder report, 2026-08-12).
  const stationName = (id: string): ReactNode => {
    const wc = workCenters.data?.items.find((c) => c.id === id);
    const label =
      wc === undefined ? undefined : localeLabel(wc.name_i18n, i18n.language) || wc.code;
    return <RefLabel value={label} id={id} pending={workCenters.isPending} />;
  };

  const columns: DataTableColumn<ShiftScoreboardRow>[] = [
    {
      // WHICH SHIFT. A date with a DAY and a NIGHT shift returns TWO rows per
      // work centre (server order is `started_at, work_center`, so they arrive
      // as two blocks of the same station names). Without this column the board
      // showed one station twice, with different numbers and nothing saying
      // which shift either belonged to — which is precisely the attribution
      // §3.7 exists to make defensible. The code rides the ROW (e17), so naming
      // a shift needs no second fetch and no second permission.
      key: "shift_definition_code",
      header: t("production.shifts.colShift"),
      id: true,
      // The name a person says ("Night"), with the pack's code kept on the
      // element for anything that needs the code itself (D-455).
      render: (row) => (
        <span data-testid="shifts-shift" data-shift-code={row.shift_definition_code}>
          {t(`production.shifts.shiftNames.${row.shift_definition_code}`, {
            defaultValue: row.shift_definition_code,
          })}
        </span>
      ),
    },
    {
      key: "work_center_id",
      header: term("work_center"),
      render: (row) => stationName(row.work_center_id),
    },
    {
      key: "qty_good",
      header: t("production.shifts.colGood"),
      numeric: true,
      render: (row) => formatQty(row.qty_good),
    },
    {
      key: "qty_scrap",
      header: t("production.shifts.colScrap"),
      numeric: true,
      render: (row) => formatQty(row.qty_scrap),
    },
    {
      key: "first_pass_yield_pct",
      header: t("production.shifts.colFpy"),
      numeric: true,
      render: (row) => (
        <span data-testid="shifts-fpy">
          {row.first_pass_yield_pct === null ? (
            <span className="shifts-no-yield">{t("production.shifts.noYield")}</span>
          ) : (
            <>
              {formatQty(row.first_pass_yield_pct)}%
              {/* The tint is an ACCELERANT. This badge is the fact: worded, in
                  the accessible name, and readable to a supervisor who cannot
                  tell the tint from the row beside it. */}
              {rowKey(row) === worstKey ? (
                <span className="shifts-worst-badge" data-testid="shifts-worst-badge">
                  {t("production.shifts.worstBadge")}
                </span>
              ) : null}
            </>
          )}
        </span>
      ),
    },
    {
      key: "downtime_minutes",
      header: t("production.shifts.colDowntime"),
      numeric: true,
      render: (row) => t("production.shifts.minutes", { n: row.downtime_minutes }),
    },
    // THE ANTI-GAMING PAIR (§3.7). `defects_handed_off` is what THIS shift
    // produced and a later one flagged; `defects_flagged` is what it found in
    // somebody else's work. They were present-and-zero from 04-C-4 precisely so
    // this slice could start writing them without the board changing shape.
    {
      key: "defects_handed_off",
      header: t("production.shifts.colHandedOff"),
      numeric: true,
      render: (row) => (
        <span data-testid="shifts-handed-off">{formatQty(row.defects_handed_off)}</span>
      ),
    },
    {
      key: "defects_flagged",
      header: t("production.shifts.colFlagged"),
      numeric: true,
      render: (row) => <span data-testid="shifts-flagged">{formatQty(row.defects_flagged)}</span>,
    },
    { key: "worker_count", header: t("production.shifts.colWorkers"), numeric: true },
    {
      key: "finalized",
      header: t("production.shifts.colState"),
      render: (row) =>
        row.finalized ? t("production.shifts.stateFinalized") : t("production.shifts.stateLive"),
    },
  ];

  return (
    <section className="shifts-screen" data-testid="shifts-screen">
      <PageHeader
        title={t("production.shifts.title")}
        actions={
          // What the two defect columns mean, one press away (D-449). It kept
          // its test id when it left the foot of the table.
          <HelpTip
            label={t("nav.helpAbout", { topic: t("production.shifts.title") })}
            data-testid="shifts-defects-pending"
          >
            {joinSentences(
              [t("production.shifts.defectsPending"), t("production.shifts.defectsScrapMoves")],
              i18n.language,
            )}
          </HelpTip>
        }
      />

      <div className="shifts-filters" data-testid="shifts-filters">
        {/* The area's ONE facility control (F11) — see components/facility
            -filter.tsx for what the two models were. No all-facilities option:
            a scoreboard reports one plant's day. */}
        <FacilityFilter
          value={effectiveFacility ?? ""}
          onChange={setFacilityId}
          facilities={facilities.data?.items ?? []}
          testId="shifts-facility"
        />
        <Field label={t("production.shifts.shiftDate")}>
          <input
            type="date"
            value={shiftDate}
            data-testid="shifts-date"
            onChange={(e) => {
              setPickedDate(e.target.value);
            }}
          />
        </Field>
      </div>
      {/* §9.7, said once where it matters rather than in a tooltip nobody
          opens: the filter is the date the SHIFT STARTS. */}
      <p className="shifts-note" data-testid="shifts-date-note">
        {t("production.shifts.shiftDateNote")}
      </p>

      {board.isPending && effectiveFacility !== undefined ? (
        <TableSkeleton rows={4} label={t("production.common.loading")} />
      ) : null}

      {board.error !== null && !(board.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("production.shifts.error")}
          retryLabel={t("production.common.retry")}
          requestIdLabel={t("production.common.requestId")}
          requestId={requestIdOf(board.error)}
          onRetry={() => {
            void board.refetch();
          }}
        />
      ) : null}

      {!board.isPending && board.error === null && rows.length === 0 ? (
        <EmptyState
          title={t("production.shifts.emptyTitle")}
          body={t("production.shifts.emptyBody")}
        />
      ) : null}

      {rows.length > 0 ? (
        <>
          <ul className="shifts-totals" data-testid="shifts-totals">
            {totals.map((total) => (
              <li key={total.facility_id}>
                <span className="shifts-total-label">{t("production.shifts.totalGood")}</span>
                <strong data-testid="shifts-total-good">{formatQty(total.qty_good)}</strong>
                <span className="shifts-total-label">{t("production.shifts.totalFpy")}</span>
                <strong data-testid="shifts-total-fpy">
                  {total.first_pass_yield_pct === null
                    ? t("production.shifts.noYield")
                    : `${formatQty(total.first_pass_yield_pct)}%`}
                </strong>
                <span className="shifts-total-label">{t("production.shifts.totalDowntime")}</span>
                <strong data-testid="shifts-total-downtime">
                  {t("production.shifts.minutes", { n: total.downtime_minutes })}
                </strong>
              </li>
            ))}
          </ul>

          <DataTable
            data-testid="shifts-table"
            columns={columns}
            rows={rows}
            rowKey={rowKey}
            empty={null}
            /* `visually-hidden`, NOT `sr-only`: this product has exactly one
               screen-reader-only utility and that is its name (styles/base.css). The
               first draft of this screen used the Tailwind spelling, which is
               defined in no stylesheet here, so the caption rendered — and at
               412 px it became a 14 × 828 px vertical strip, one character per
               line, above the table. A class name that does not exist fails
               silently and looks fine on a desktop. The primitive owns the
               caption element now and only the register's one utility can
               reach it. */
            caption={t("production.shifts.tableCaption")}
            rowTestId="shifts-row"
            rowClassName={(row) =>
              rowKey(row) === worstKey ? "shifts-row shifts-row-worst" : "shifts-row"
            }
            rowProps={(row) => ({ "data-work-center": row.work_center_id })}
          />
        </>
      ) : null}

      {/* §4's S5 pairs the scoreboard with the defect hand-off LEDGER, which
          is real as of 04-C-5. It is scoped to the shift instances ON SCREEN —
          ALL of them, including both halves of a day/night date — not to the
          facility at large: this panel answers "what did these shifts hand each
          other", and a facility-wide list under a one-date board would be two
          different questions stacked. It renders nothing for a role without
          `production.approve_shift_handoff`, which e18 requires to read.

          `boardPending` tells the panel whether an undefined `query` means
          "board still resolving" (skeleton) or "this date has no shift
          instances" (empty state) — without it every empty day wore a
          permanent skeleton, because the panel's own disabled query stays
          `isPending` forever (W-SWEEP, 2026-08-14). "Resolving" includes the
          facility lookup: while `facilities` is in flight the board query is
          disabled and ITS `isPending` would report forever-pending too. */}
      <HandoffLedger
        query={handoffQuery}
        boardPending={facilities.isPending || (effectiveFacility !== undefined && board.isPending)}
        canDispose={permissions.has("production.approve_shift_handoff")}
      />
    </section>
  );
}
